// MIT License
//
// Copyright (c) 2019 Virtru Corporation
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { h, Component } from 'preact';
import PropTypes from 'prop-types';
import {
  activateCode, getEmailFromQuery, loginWith, requestCode,
  AUTH_TYPE_GOOGLE, AUTH_TYPE_O365, AUTH_TYPE_OUTLOOK
} from './virtruHandler';
import Authenticated from './Authenticated';
import AuthSelector from './AuthSelector';
import EmailCode from './EmailCode';
import EmailEntry from './EmailEntry';
import ErrorMessage from './ErrorMessage';
import Header from './Header';
import Footer from './Footer';
import Loader from './Loader';

export const PAGE_ENTER_EMAIL = 'PAGE_ENTER_EMAIL';
export const PAGE_SELECT_AUTH = 'PAGE_SELECT_AUTH';
export const PAGE_ENTER_CODE = 'PAGE_ENTER_CODE';
export const PAGE_AUTHENTICATED = 'PAGE_AUTHENTICATED';

const defaultPage = PAGE_ENTER_EMAIL;
const defaultAfterAuthTimeout = 2000;

class App extends Component {
  constructor() {
    super();

    // TODO: Remove when fix to proxy is deployed
    const supportedOrigins = [
      '^https://demos.developer.virtru.com',
      '^https://local.virtru.com'
    ];
    const oauthSupported = supportedOrigins.some(origin => !!`${window.location.protocol}//${window.location.host}`.match(new RegExp(origin)));

    this.state = {
      email: '',
      loading: false,
      page: defaultPage,
      oauthSupported
    };

    this.afterAuth = this.afterAuth.bind(this);
    this.clearError = this.clearError.bind(this);
    this.disableLoader = this.disableLoader.bind(this);
    this.enableLoader = this.enableLoader.bind(this);
    this.handleActivateCode = this.handleActivateCode.bind(this);
    this.handleEmailSubmit = this.handleEmailSubmit.bind(this);
    this.handleOnBack = this.handleOnBack.bind(this);
    this.handleSelectAuth = this.handleSelectAuth.bind(this);
    this.renderPage = this.renderPage.bind(this);
    this.showError = this.showError.bind(this);
  }

  componentDidMount() {
    const { page } = this.state;
    const { startPage } = this.props;
    const email = getEmailFromQuery();
    if (!this.isAuthenticated(email)) {
      if (startPage && startPage !== page) {
        this.setState({ page: startPage });
      }
    }
  }

  showError(error) {
    this.setState({ error });
  }

  clearError() {
    this.setState({ error: null });
  }

  enableLoader() {
    this.setState({ loading: true });
  }

  disableLoader() {
    this.setState({ loading: false });
  }

  async handleActivateCode(code) {
    const { email } = this.state;
    const { redirectUrl, authOptions = {} } = this.props;
    this.setState({ error: false });
    this.enableLoader();
    try {
      // TODO: enable when async is working
      // await activateCode(email, code, redirectUrl);

      // TODO: Remove this when async is working
      await new Promise(async (resolve, reject) => {
        let tries = 0;
        const maxTries = 5;
        await activateCode(email, code, redirectUrl, authOptions);
        const intervalHandle = setInterval(() => {
          if (Virtru.Auth.isLoggedIn({ email, ...authOptions })) {
            clearInterval(intervalHandle);
            resolve();
          } else if (tries++ > maxTries) {
            clearInterval(intervalHandle);
            reject();
          }
        }, 2500);
      });
      // /TODO
      this.disableLoader();
      this.afterAuth();
    } catch (e) {
      this.disableLoader();
      this.setState({ error: 'Could not activate with the code provided. Please check the code and try agian.' });
    }
  }

  async handleSelectAuth(auth) {
    const { email } = this.state;
    const { redirectUrl, authOptions = {} } = this.props;
    this.enableLoader();
    switch (auth) {
      case 'google':
        await loginWith(AUTH_TYPE_GOOGLE, email, redirectUrl, authOptions);
        // this.disableLoader();
        break;
      case 'office365':
        await loginWith(AUTH_TYPE_O365, email, redirectUrl, authOptions);
        // this.disableLoader();
        break;
      case 'outlook':
        await loginWith(AUTH_TYPE_OUTLOOK, email, redirectUrl, authOptions);
        // this.disableLoader();
        break;
      case 'email':
        await requestCode(email, authOptions);
        this.setState({ page: PAGE_ENTER_CODE, loading: false });
        break;
      default:
        this.showError('Please try a different authentication mechanism.');
        break;
    }
  }

  async handleEmailSubmit(email) {
    this.setState({ email, loading: true });
    if (!this.isAuthenticated(email)) {
      // TODO: Check if oauth is supported for domain needs deployment to proxy
      // const oauthSupported = await Virtru.Auth.isFederatedAuthAllowed();
      this.nextPage(PAGE_SELECT_AUTH);
    }
  }

  handleOnBack() {
    const { page } = this.state;
    switch (page) {
      case PAGE_SELECT_AUTH:
        return this.setState({ page: PAGE_ENTER_EMAIL });
      case PAGE_ENTER_CODE:
        return this.setState({ page: PAGE_SELECT_AUTH });
      default:
        return this.setState({ page: PAGE_ENTER_EMAIL });
    }
  }

  showBack() {
    const { loading, page } = this.state;
    const { startPage } = this.props;
    if (loading) {
      return false;
    } else if (page === PAGE_AUTHENTICATED) {
      return false;
    } else if (startPage) {
      return startPage !== page;
    }
    return defaultPage !== page;
  }

  isAuthenticated(email) {
    const { authOptions = {} } = this.props;

    if (email && Virtru.Auth.isLoggedIn({ email, ...authOptions })) {
      this.setState({ email, page: PAGE_AUTHENTICATED, loading: false });
      this.afterAuth();
      return true;
    }
    return false;
  }

  afterAuth(skipTimeout = false) {
    const { email, page, timeoutHandle } = this.state;
    const { afterAuth, afterAuthTimeout } = this.props;

    if (page !== PAGE_AUTHENTICATED) {
      this.setState({ page: PAGE_AUTHENTICATED });
    }

    if (typeof afterAuth === 'function') {
      const afterAuthFn = () => afterAuth(email);
      if (skipTimeout) {
        clearTimeout(timeoutHandle);
        afterAuthFn();
      } else {
        const timeoutHandleAfter = setTimeout(afterAuthFn, (afterAuthTimeout && parseInt(afterAuthTimeout)) || defaultAfterAuthTimeout);
        this.setState({ timeoutHandleAfter });
      }
    }
  }

  nextPage(page) {
    this.setState({ loading: false, page });
  }

  renderPage() {
    const { email, loading, page, oauthSupported } = this.state;
    if (loading) return <Loader />;
    switch (page) {
      case PAGE_ENTER_EMAIL:
        return <EmailEntry email={email} onSubmitEmail={this.handleEmailSubmit} />;
      case PAGE_SELECT_AUTH:
        return <AuthSelector email={email} onSelectAuth={this.handleSelectAuth} oauthSupported={oauthSupported} />;
      case PAGE_ENTER_CODE:
        return <EmailCode onActivateCode={this.handleActivateCode} />;
      case PAGE_AUTHENTICATED:
        return <Authenticated />;
      default:
        return <Authenticated />;
    }
  }

  render() {
    const {
      error,
    } = this.state;

    return (
      <div className='virtru-auth'>
        <Header showBack={this.showBack()} onBack={this.handleOnBack} />
        <div className='virtru-auth-content'>
          { error ? <ErrorMessage message={error} /> : null }
          {this.renderPage()}
        </div>
        <Footer />
      </div>
    );
  }
}

App.propTypes = {
  afterAuth: PropTypes.func,
  afterAuthTimeout: PropTypes.number,
  redirectUrl: PropTypes.string
};

export default App;
